import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Paper, Avatar,
  List, ListItem, ListItemAvatar, ListItemText, Divider, Button,
  Chip, IconButton, Badge, LinearProgress, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  PersonAdd as PersonAddIcon,
  Boy as BoyIcon,
  Girl as GirlIcon,
  DirectionsBus as BusIcon,
  CurrencyRupee as RupeeIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Announcement as AnnouncementIcon,
  Cake as CakeIcon,
  AccessTime as ClockIcon,
  Refresh as RefreshIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend, ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { dashboardAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TeacherDashboard from './TeacherDashboard';
import StudentDashboard from './StudentDashboard';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// ── Modern Stat Card with Gradient ──
const StatCard = ({ title, value, icon, color, subtitle, trend, gradient }) => (
  <Card sx={{
    height: '100%',
    background: gradient || `linear-gradient(135deg, ${color}12 0%, ${color}05 100%)`,
    borderLeft: `4px solid ${color}`,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px) !important',
      boxShadow: `0 8px 24px ${color}25 !important`,
    }
  }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.8, opacity: 0.7
          }}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{
            my: 0.5, color: 'text.primary',
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {value}
          </Typography>
          {subtitle && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend === 'up' && <TrendingUpIcon sx={{ fontSize: 14, color: '#4caf50' }} />}
              {trend === 'down' && <TrendingDownIcon sx={{ fontSize: 14, color: '#e53935' }} />}
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{subtitle}</Typography>
            </Box>
          )}
        </Box>
        <Avatar sx={{
          bgcolor: `${color}20`,
          color,
          width: 52, height: 52,
          boxShadow: `0 4px 12px ${color}30`,
          transition: 'transform 0.3s ease',
          '&:hover': { transform: 'scale(1.1) rotate(5deg) !important' }
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState({ students: [], payments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, activityRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getActivities()
      ]);
      setStats(statsRes.data.data);
      setActivities(activityRes.data.data);
    } catch (err) {
      console.error('Dashboard load failed', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Role-based dashboard routing (after all hooks)
  if (user?.role === 'teacher') {
    return <TeacherDashboard />;
  }
  if (user?.role === 'student') {
    return <StudentDashboard />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} sx={{ color: '#1a9e8f' }} />
        <Typography variant="body1" color="text.secondary">Loading dashboard data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchData}>Retry</Button>
        }>{error}</Alert>
      </Box>
    );
  }

  if (!stats) return null;

  const { counts, attendance, classWise = [], revenueChart = [], currentAcademicYear } = stats;
  const recentAdmissions = activities.students || [];

  // Derived values
  const totalStudents = counts.students || 0;
  const boys = counts.boys || 0;
  const girls = counts.girls || 0;
  const newAdmissions = counts.newAdmissions || 0;
  const attendanceTotal = attendance.present + attendance.absent + attendance.leave;
  const attendancePct = attendanceTotal > 0 ? Math.round((attendance.present / totalStudents) * 100) : 0;
  const totalFeeCollected = counts.totalFeeCollected || 0;
  const todayCollection = counts.todayCollection || 0;
  const monthlyCollection = counts.collection || 0;
  const transportStudents = counts.transportStudents || 0;
  const busCount = counts.busCount || 0;

  // Chart data
  const feeChartData = {
    labels: revenueChart.map(d => d.month),
    datasets: [{
      label: 'Fee Collection',
      data: revenueChart.map(d => d.amount),
      backgroundColor: '#1a9e8f',
      borderRadius: 4,
      barThickness: 28,
    }]
  };

  const attendanceChartData = {
    labels: ['Present', 'Absent', 'Leave'],
    datasets: [{
      data: [attendance.present, attendance.absent, attendance.leave],
      backgroundColor: ['#4caf50', '#e53935', '#ff9800'],
      borderWidth: 0,
    }]
  };

  const genderChartData = {
    labels: ['Boys', 'Girls'],
    datasets: [{
      data: [boys, girls],
      backgroundColor: ['#2196f3', '#e91e63'],
      borderWidth: 0,
    }]
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '₹0';
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <Box>
      {/* ── Header ── */}
      <Card sx={{
        mb: 3, overflow: 'visible',
        background: 'linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a9e8f 100%) !important',
        border: 'none !important',
        color: '#fff !important'
      }}>
        <CardContent sx={{ p: '16px 20px !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff !important', letterSpacing: -0.5 }}>
                School Dashboard
              </Typography>
              {currentAcademicYear && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7) !important', mt: 0.5 }}>
                  Academic Year: <strong style={{ color: '#4db6ac' }}>{currentAcademicYear}</strong>
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={fetchData} title="Refresh" sx={{ color: '#fff !important', '&:hover': { background: 'rgba(255,255,255,0.1) !important' } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
            <Chip
              icon={<CalendarIcon sx={{ fontSize: 14, color: '#4db6ac !important' }} />}
              label={new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.1) !important', color: '#fff !important', border: '1px solid rgba(255,255,255,0.2) !important' }}
            />
            <Chip
              icon={<ClockIcon sx={{ fontSize: 14, color: '#4db6ac !important' }} />}
              label={new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.1) !important', color: '#fff !important', border: '1px solid rgba(255,255,255,0.2) !important' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* ── Top Stat Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Total Students" value={totalStudents}
            icon={<PeopleIcon />} color="#1a9e8f"
            subtitle={newAdmissions > 0 ? `${newAdmissions} new this month` : 'No new admissions'} trend={newAdmissions > 0 ? 'up' : undefined} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Today's Attendance" value={attendanceTotal > 0 ? `${attendancePct}%` : 'N/A'}
            icon={<CheckIcon />} color="#4caf50"
            subtitle={attendanceTotal > 0 ? `${attendance.present} / ${totalStudents} present` : 'Not marked yet'} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Monthly Collection" value={formatCurrency(monthlyCollection)}
            icon={<RupeeIcon />} color="#ff9800"
            subtitle={`Today: ${formatCurrency(todayCollection)}`} trend={todayCollection > 0 ? 'up' : undefined} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Transport" value={transportStudents}
            icon={<BusIcon />} color="#2196f3"
            subtitle={`${busCount} vehicles active`} />
        </Grid>
      </Grid>

      {/* ── Staff + Gender Split + Today's Collection ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Gender Distribution</Typography>
              {(boys > 0 || girls > 0) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Box sx={{ width: 120, height: 120 }}>
                    <Doughnut data={genderChartData} options={{ cutout: '65%', plugins: { legend: { display: false } } }} />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BoyIcon sx={{ color: '#2196f3', fontSize: 20 }} />
                      <Typography variant="body2">Boys: <strong>{boys}</strong></Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <GirlIcon sx={{ color: '#e91e63', fontSize: 20 }} />
                      <Typography variant="body2">Girls: <strong>{girls}</strong></Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupsIcon sx={{ color: '#666', fontSize: 20 }} />
                      <Typography variant="body2">Total: <strong>{totalStudents}</strong></Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>No student data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Attendance Breakdown</Typography>
              {attendanceTotal > 0 ? (
                <Box sx={{ width: 120, height: 120, mx: 'auto', mt: 1 }}>
                  <Doughnut data={attendanceChartData} options={{ cutout: '65%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } } }} />
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Typography variant="body2" color="text.secondary">Today's attendance not marked yet</Typography>
                  <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => navigate('/attendance')}>
                    Mark Attendance
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #1a9e8f 0%, #0d7377 50%, #14532d 100%) !important',
            border: 'none !important',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -20, right: -20,
              width: 100, height: 100,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -30, left: -30,
              width: 120, height: 120,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
            }
          }}>
            <CardContent sx={{ color: '#fff' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ opacity: 0.9 }}>Today's Collection</Typography>
              <Typography variant="h3" fontWeight={700} sx={{ my: 1 }}>{formatCurrency(todayCollection)}</Typography>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>This Month</Typography>
                  <Typography variant="body2" fontWeight={700}>{formatCurrency(monthlyCollection)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Collected</Typography>
                  <Typography variant="body2" fontWeight={700}>{formatCurrency(totalFeeCollected)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Fee Collection Trend + Class-wise Strength ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Fee Collection Trend (Last 6 Months)</Typography>
              {revenueChart.length > 0 && revenueChart.some(d => d.amount > 0) ? (
                <Box height={220}>
                  <Bar data={feeChartData} options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { y: { beginAtZero: true, grid: { color: '#f0f0f0' }, ticks: { callback: v => `₹${v / 1000}K` } }, x: { grid: { display: false } } }
                  }} />
                </Box>
              ) : (
                <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No fee collection data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: '12px !important' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ px: 1, pb: 1 }}>Class-wise Strength</Typography>
              {classWise.length > 0 ? (
                <TableContainer sx={{ maxHeight: 260, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ py: 0.5, fontSize: 11 }}>Class</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: 11 }}>Total</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: 11 }}>Boys</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: 11 }}>Girls</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: 11 }}>Present</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: 11 }}>%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classWise.map(c => {
                        const pct = c.total > 0 ? Math.round((c.present / c.total) * 100) : 0;
                        return (
                          <TableRow key={c.classId || c.class} hover>
                            <TableCell sx={{ py: 0.5, fontWeight: 600, fontSize: 12 }}>{c.class}</TableCell>
                            <TableCell align="center" sx={{ py: 0.5, fontSize: 12 }}>{c.total}</TableCell>
                            <TableCell align="center" sx={{ py: 0.5, fontSize: 12, color: '#2196f3' }}>{c.boys}</TableCell>
                            <TableCell align="center" sx={{ py: 0.5, fontSize: 12, color: '#e91e63' }}>{c.girls}</TableCell>
                            <TableCell align="center" sx={{ py: 0.5, fontSize: 12 }}>{c.present}</TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LinearProgress variant="determinate" value={pct}
                                  sx={{
                                    flexGrow: 1, height: 5, borderRadius: 3, bgcolor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': { bgcolor: pct >= 90 ? '#4caf50' : pct >= 70 ? '#ff9800' : '#e53935' }
                                  }} />
                                <Typography variant="caption" sx={{ fontSize: 10, minWidth: 28 }}>{pct}%</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals Row */}
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ py: 0.5, fontWeight: 700, fontSize: 12 }}>Total</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontWeight: 700, fontSize: 12 }}>
                          {classWise.reduce((sum, c) => sum + c.total, 0)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontWeight: 700, fontSize: 12, color: '#2196f3' }}>
                          {classWise.reduce((sum, c) => sum + c.boys, 0)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontWeight: 700, fontSize: 12, color: '#e91e63' }}>
                          {classWise.reduce((sum, c) => sum + c.girls, 0)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontWeight: 700, fontSize: 12 }}>
                          {classWise.reduce((sum, c) => sum + c.present, 0)}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {(() => {
                            const totalAll = classWise.reduce((sum, c) => sum + c.total, 0);
                            const totalPresent = classWise.reduce((sum, c) => sum + c.present, 0);
                            const totalPct = totalAll > 0 ? Math.round((totalPresent / totalAll) * 100) : 0;
                            return <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>{totalPct}%</Typography>;
                          })()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No class data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Recent Admissions + Quick Links ── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: '12px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>Recent Admissions</Typography>
                <Button size="small" onClick={() => navigate('/students')}>View All</Button>
              </Box>
              {recentAdmissions.length > 0 ? (
                <List dense disablePadding>
                  {recentAdmissions.slice(0, 5).map((s, i) => (
                    <React.Fragment key={s.id || i}>
                      <ListItem sx={{ px: 1 }}>
                        <ListItemAvatar sx={{ minWidth: 36 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: s.gender === 'M' ? '#2196f3' : '#e91e63' }}>
                            {s.name ? s.name[0] : '?'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={600}>{s.name}</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary">{s.class}{s.section ? ` - ${s.section}` : ''} • {s.date}</Typography>}
                        />
                        <Chip label="New" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
                      </ListItem>
                      {i < Math.min(recentAdmissions.length, 5) - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No recent admissions</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Quick Actions</Typography>
              <Grid container spacing={1}>
                {[
                  { label: 'Fees', icon: <RupeeIcon />, path: '/fees', color: '#ff9800' },
                  { label: 'Attendance', icon: <CheckIcon />, path: '/attendance', color: '#4caf50' },
                  { label: 'Students', icon: <PeopleIcon />, path: '/students', color: '#1a9e8f' },
                  { label: 'Certificates', icon: <SchoolIcon />, path: '/certificates', color: '#9c27b0' },
                  { label: 'Reports', icon: <TrendingUpIcon />, path: '/reports', color: '#2196f3' },
                  { label: 'Exams', icon: <SchoolIcon />, path: '/exams/setup', color: '#e53935' },
                  { label: 'SMS', icon: <AnnouncementIcon />, path: '/sms', color: '#00bcd4' },
                  { label: 'Birthdays', icon: <CakeIcon />, path: '/reports/birthdays', color: '#e91e63' },
                ].map((action) => (
                  <Grid item xs={3} key={action.label}>
                    <Box
                      onClick={() => navigate(action.path)}
                      sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        p: 1.5, borderRadius: 2, cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '1px solid transparent',
                        '&:hover': {
                          bgcolor: `${action.color}08`,
                          transform: 'translateY(-4px)',
                          border: `1px solid ${action.color}30`,
                          boxShadow: `0 4px 12px ${action.color}15`
                        }
                      }}
                    >
                      <Avatar sx={{
                        bgcolor: `${action.color}15`, color: action.color,
                        width: 44, height: 44, mb: 0.5,
                        transition: 'all 0.3s ease',
                        '& .MuiSvgIcon-root': { fontSize: 22 }
                      }}>
                        {action.icon}
                      </Avatar>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{action.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Staff Summary Card ── */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: '16px !important' }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#1a9e8f">{counts.staff || 0}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Staff</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#2196f3">{counts.teachers || 0}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Teachers</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#ff9800">{counts.hostelCapacity || 0}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Hostel Capacity</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#e53935">{counts.lowStock || 0}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Low Stock Items</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
